import { Controller } from "@hotwired/stimulus"
import consumer from "channels/consumer"
import { logger } from "../logger"
import { trackEvent } from "../umami"

// Handles real-time report generation status updates via ActionCable
// Replaces: app/assets/javascripts/channels/report_channel.js
export default class extends Controller {
  static targets = ["status", "message", "close"]
  static values = {
    reportCacheId: Number
  }

  connect() {
    // Track if we've started cleanup to avoid duplicate processing
    this.isClosing = false

    if (this.hasReportCacheIdValue) {
      this.subscription = consumer.subscriptions.create(
        {
          channel: "ReportChannel",
          report_cache_id: this.reportCacheIdValue
        },
        {
          connected: this.connected.bind(this),
          disconnected: this.disconnected.bind(this),
          received: this.received.bind(this)
        }
      )
    }
  }

  disconnect() {
    // Only unsubscribe here if we're NOT in manual close flow
    // When manually closing, close() handles unsubscribe with proper timing
    if (this.subscription && !this.manualCloseInProgress) {
      this.subscription.unsubscribe()
    }
  }

  connected() {
    // Request current status when connected (in case job finished during connection)
    this.subscription.perform('check_status')
  }

  disconnected() {
    // ActionCable connection closed
  }

  received(data) {
    // DEBUG: Log all received messages
    logger.log('📨 ReportChannel received:', {
      reportCacheId: this.reportCacheIdValue,
      data: data,
      isClosing: this.isClosing
    })
    
    // Ignore duplicate completion messages (server sends 2 broadcasts on completion)
    if (this.isClosing) {
      logger.log('⏭️ Ignoring message - already closing')
      return
    }

    if (data.status === 'done' || data.status === 'failed') {
      if (data.status === 'failed') {
        
        // Hide and remove spinner completely
        if (this.hasStatusTarget) {
          this.statusTarget.remove()
          logger.log('✅ Status target removed')
        }
        
        // Replace message area content with error message (clear first to ensure clean replacement)
        if (this.hasMessageTarget && data.message) {
          this.messageTarget.textContent = ''
          this.messageTarget.innerHTML = `<span class="text-warning"><b>${data.message}</b></span>` 
        }
        
        // Auto-close after 3 seconds and destroy the report cache
        this.isClosing = true
        setTimeout(() => {
          // Tell server to destroy the failed report cache via HTTP (more reliable than WebSocket)
          fetch(`/report_caches/${this.reportCacheIdValue}/close_report`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
            }
          }).catch(error => {
            logger.error('Failed to close report cache:', error)
          })
          if (this.subscription) {
            this.subscription.unsubscribe()
          }
          this.closeStatus()
        }, 3000)
      } else if (data.url) {
        // Success with download URL - check if download already initiated
        if (data.download_initiated) {
          logger.log('⏭️ Download already initiated for report', this.reportCacheIdValue)
          // Just close the status - download already started elsewhere
          this.isClosing = true
          this.closeStatus()
          return
        }

        // Update message first
        if (this.hasMessageTarget && data.message) {
          this.messageTarget.textContent = data.message
        }
        
        // Set download_initiated flag on server before starting download
        this.initiateDownload(data.url)
      }
      // Note: If status is 'done' but no URL, this is likely a duplicate broadcast - ignore it
    } else {
      // Update progress message - handle both 'progress' and other statuses
      if (this.hasMessageTarget && data.message) {
        this.messageTarget.textContent = data.message
      }
    }
  }

  // Close button clicked
  close(event) {
    logger.log('🚫 close() called, event:', event)
    event.preventDefault()
    if (this.subscription) {
      // Set flag to prevent disconnect() from unsubscribing immediately
      this.manualCloseInProgress = true
      // Tell server to destroy the report_cache via HTTP (more reliable than WebSocket)
      logger.log('🔴 Closing report, sending close request via fetch for cache ID:', this.reportCacheIdValue)
      fetch(`/report_caches/${this.reportCacheIdValue}/close_report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      }).then(() => {
        logger.log('✅ Close request completed')
      }).catch(error => {
        logger.error('❌ Close request failed:', error)
      })
      // Delay cleanup to ensure HTTP request is sent
      setTimeout(() => {
        if (this.subscription) {
          this.subscription.unsubscribe()
        }
        this.closeStatus()
      }, 100)
    } else {
      this.closeStatus()
    }
  }

  async initiateDownload(downloadUrl) {
    try {
      // Set download_initiated flag on server
      const response = await fetch(`/report_caches/${this.reportCacheIdValue}/initiate_download`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })

      const result = await response.json()
      
      if (result.success) {
        // Flag set successfully, proceed with download
        this.isClosing = true
        this.startDownload(downloadUrl)
      } else {
        // Failed to set flag (probably already set by another tab)
        logger.log('⏭️ Download already initiated by another tab')
        this.isClosing = true
        this.closeStatus()
      }
    } catch (error) {
      logger.error('Error initiating download:', error)
      // On error, still try to download (fall back to old behavior)
      this.isClosing = true
      this.startDownload(downloadUrl)
    }
  }

  async startDownload(downloadUrl) {
    // Track successful report download
    trackEvent('Report Downloaded')

    logger.log('📥 Starting download via fetch:', downloadUrl)

    try {
      // Fetch file as blob - this doesn't navigate the page
      const response = await fetch(downloadUrl, {
        credentials: 'same-origin'
      })

      if (!response.ok) {
        throw new Error(`Download failed: ${response.status}`)
      }

      // Extract filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition')
      let filename = 'report.xlsx'
      if (contentDisposition) {
        // Handle UTF-8 encoded filename: filename*=UTF-8''encoded_name
        const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)
        if (utf8Match) {
          filename = decodeURIComponent(utf8Match[1])
        } else {
          // Fallback to regular filename
          const match = contentDisposition.match(/filename="?([^";\n]+)"?/i)
          if (match) {
            filename = match[1]
          }
        }
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)

      // Create anchor and trigger download
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      link.style.display = 'none'
      document.body.appendChild(link)
      link.click()

      // Clean up
      setTimeout(() => {
        link.remove()
        window.URL.revokeObjectURL(url)
      }, 100)

      logger.log('📥 Download triggered successfully:', filename)
    } catch (error) {
      logger.error('📥 Download failed:', error)
    }

    this.closeStatus()
  }

  closeStatus() {
    // Clean up sessionStorage
    this.removeFromSessionStorage()

    // Get container before removing element
    const container = document.getElementById('reportStatuses')
    
    // Remove this report status
    this.element.remove()

    // Use requestAnimationFrame to ensure DOM is updated after element removal
    requestAnimationFrame(() => {
      if (container) {
        const remainingStatuses = container.querySelectorAll('.report-status').length
        if (remainingStatuses === 0) {
          // No more statuses - remove container and reset height immediately
          container.remove()
          document.documentElement.style.setProperty('--report-status-overlay-height', '0px')
        } else {
          // Still have statuses - update height
          if (window.updateReportStatusOverlayHeight) {
            window.updateReportStatusOverlayHeight()
          } else {
            // Fallback: calculate height directly
            if (container.classList.contains('show')) {
              const height = container.offsetHeight
              document.documentElement.style.setProperty('--report-status-overlay-height', height + 'px')
            }
          }
        }
      } else {
        // Container doesn't exist - ensure height is reset
        document.documentElement.style.setProperty('--report-status-overlay-height', '0px')
      }
    })
  }

  removeFromSessionStorage() {
    // Remove this report cache ID from sessionStorage
    try {
      let activeReports = JSON.parse(sessionStorage.getItem('activeReportCaches') || '[]')
      activeReports = activeReports.filter(id => id !== this.reportCacheIdValue)
      sessionStorage.setItem('activeReportCaches', JSON.stringify(activeReports))
    } catch (error) {
      logger.error('Error removing report from sessionStorage:', error)
    }
  }

  isMobileOrTablet() {
    return /(android|iphone|ipad|mobile)/i.test(navigator.userAgent)
  }
}
